<template>
  <ResponsiveLayout
    :progress="progress"
    :show-lang-picker="false"
    sticky-actions
    theme="blue-actions"
  >
    <template #header_start>
      <ButtonBack @click="emit('back')" />
    </template>
    <template #header_middle>
      <div class="kds-typography-title-medium">
        {{ $t('b2b_self_test_generic_assessment') }}
      </div>
    </template>
    <div class="kds-main-wrapper">
      <h3
        class="kds-typography-title-xlarge"
        v-html="$t('b2b__red_flags__sub_title')"
      />
    </div>
    <div
      v-for="(item, index) in items"
      :key="index"
      class="red-flag"
    >
      <div class="red-flag-question">
        <div
          class="red-flag-question__title"
          v-html="item.question"
        />
        <DropdownArrowControl
          :css-classes="['dropdown-arrow-control-red-flag']"
          :default-state="item.checked"
          :disabled="formIsDisabled"
          @click="
            (nextState) => {
              switchMedicalConditionControl(nextState, item)
            }
          "
        />
      </div>
      <div :class="getRedFlagAnswerCSSClasses(item)">
        <div
          :aria-hidden="!item.checked"
          class="red-flag-description"
          v-html="item.answer"
        />
      </div>
    </div>

    <template #actions>
      <CheckboxField
        data-qa="precautions-red-flag-checkbox"
        :label="redFlagCheckboxLabel"
        :model-value="redFlagApproved"
        @update:model-value="handleConfirmationBoxChange"
      />
      <BaseButton
        :disabled="!isFormValid || formIsDisabled"
        :text="submitButtonText"
        data-qa="submit"
        @click="next"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import DropdownArrowControl from '@shared/components/DropdownArrowControl.vue'
import { sendCrmEvent } from '@shared/api-endpoints'
import env from '@shared/env.js'

import precautions from '@shared/modules/assessment/config/precautions-msk-eu.json'
import { useI18n } from 'petite-vue-i18n'
import useSelfTestStore from '@shared/modules/assessment/stores/useSelfTestStore.js'
import useUserStore from '@shared/stores/useUserStore'
import { computed, ref } from 'vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import CheckboxField from '@shared/components/form/CheckboxField.vue'

const { t } = useI18n()
const selfTestStore = useSelfTestStore()
const userStore = useUserStore()

const props = defineProps({
  progress: {
    type: Number,
    default: null,
  },
  handleNextEvent: {
    type: Function,
    default: () => {},
  },
})

const isPreSubscriptionFlow = !userStore.user?.currentSubscription

const emit = defineEmits(['back'])

const items = ref(
  precautions
    .map((precaution) => ({
      question: t(precaution.question),
      answer: t(precaution.answer),
    }))
    .filter((item) => item.question !== '' || item.answer !== ''),
)

const redFlagApproved = ref(selfTestStore.redFlagApproved)

const isFormValid = computed(() => redFlagApproved.value)

const redFlagCheckboxLabel = computed(() => {
  if (isPreSubscriptionFlow) {
    return t('eu__red_flags__checkbox_label__pre_activation')
  }
  return t('b2b__red_flags__checkbox_label')
})

const submitButtonText = computed(() => {
  if (isPreSubscriptionFlow) {
    return t('eu__precautions__submit_button_text__pre_activation')
  }
  // On MSK EU more steps follow the red flags.
  if (env.isMskEu) return t('generic_action_continue')
  return t('b2b_sign_up_submit_button_text')
})

const handleConfirmationBoxChange = (checked) => {
  redFlagApproved.value = checked
}

const formIsDisabled = ref(false)

const next = async () => {
  if (formIsDisabled.value) {
    return
  }
  if (!isFormValid.value) return
  formIsDisabled.value = true

  // The approval is the one answer on this screen; its text is the checkbox
  // label.
  selfTestStore.answer('red_flag_approved', {
    value: 1,
    text: redFlagCheckboxLabel.value,
    questionText: t('b2b__red_flags__sub_title'),
  })

  await sendCrmEvent({
    name: 'milestone_red_flags_approved',
  })

  props.handleNextEvent()
}

const getRedFlagAnswerCSSClasses = (item) => [
  'red-flag-answer',
  !item.checked ? 'red-flag-answer--hidden' : '',
]

const switchMedicalConditionControl = (nextState, item) => {
  item.checked = nextState
}
</script>

<style lang="scss">
.dropdown-arrow-control-red-flag {
  width: 24px;
  height: 20px;
}

.red-flag {
  border-bottom: 1px solid $kds-color-background-subtle;
  overflow: hidden;
  min-height: 56px;
}

.red-flag-question {
  display: flex;
  justify-content: space-between;
  position: relative;

  padding: 18px 0;
  margin-right: 6px;

  &__title {
    flex-basis: 80%;

    @include kds-typography-title-medium;
  }
}

.red-flag-answer {
  opacity: 1;
  transition: opacity 0.75s linear;

  &--hidden {
    opacity: 0;
    height: 0;
  }
}

.red-flag-description {
  @include kds-typography-paragraph-medium;
}
</style>
